import React from "react";
import { Alert, Button, Card, CardBody, Col, Form, Input, Row, Table } from "reactstrap";
import { FACTURA_CAMPOS, TOTAL_DIGITOS_FACTURA } from "../constants";
import { construirIdFacturaAnulada, formatearFacturaSegmento } from "../helpers";
import { FacturaAnulada } from "../types";

interface FacturasAnuladasStepProps {
  nuevaFacturaAnulada: FacturaAnulada;
  facturasAnuladas: FacturaAnulada[];
  puedeEditar: boolean;
  onNuevaFacturaChange: (campo: keyof FacturaAnulada, valor: string) => void;
  onAgregarFactura: () => void;
  onEliminarFactura: (id: number) => void;
  onEditarFactura?: (factura: FacturaAnulada) => void;
}

const FacturasAnuladasStep = ({
  nuevaFacturaAnulada,
  facturasAnuladas,
  puedeEditar,
  onNuevaFacturaChange,
  onAgregarFactura,
  onEliminarFactura,
  onEditarFactura,
}: FacturasAnuladasStepProps) => (
  <Row className="g-4">
    <Col>
      <Card>
        <CardBody>
          {!puedeEditar ? (
            <Alert color="warning">Necesitás seleccionar una caja antes de cargar facturas anuladas.</Alert>
          ) : (
            <>
              <Form
                onSubmit={(event) => {
                  event.preventDefault();
                  onAgregarFactura();
                }}
              >
                <Table className="align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: "15%" }}>Factura anulada</th>
                      {FACTURA_CAMPOS.map((campo) => (
                        <th key={campo.campo}>{campo.etiqueta}</th>
                      ))}
                      <th>Motivo</th>
                      <th style={{ width: "12%" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="fw-semibold">Nueva entrada</td>
                      {(() => {
                        let offset = 0;
                        return FACTURA_CAMPOS.map((campo) => {
                          const longitud = campo.longitud;
                          const valores = nuevaFacturaAnulada[campo.campo] ?? "";
                          const digitos = Array.from({ length: longitud }, (_, idx) => valores[idx] ?? "");
                          const inicioGlobal = offset;
                          offset += longitud;
                          return (
                            <td key={`anulada-${campo.campo}`}>
                              <div className="d-flex gap-1 flex-wrap">
                                  {digitos.map((digito, idx) => {
                                    const indiceGlobal = inicioGlobal + idx;
                                    return (
                                      <Input
                                        key={`anulada-${campo.campo}-${idx}`}
                                        id={construirIdFacturaAnulada(indiceGlobal)}
                                        type="text"
                                        value={digito}
                                        maxLength={1}
                                        inputMode="numeric"
                                        onChange={(event) => {
                                        const texto = event.target.value.replace(/[^0-9]/g, "").slice(0, 1);
                                        const actual = nuevaFacturaAnulada[campo.campo] ?? "";
                                        const chars = Array.from({ length: longitud }, (_, pos) => actual[pos] ?? "");
                                        chars[idx] = texto;
                                        onNuevaFacturaChange(campo.campo, chars.join(""));
                                        if (texto) {
                                          const siguiente = indiceGlobal + 1;
                                          if (siguiente < TOTAL_DIGITOS_FACTURA) {
                                            const ref = document.getElementById(
                                              construirIdFacturaAnulada(siguiente),
                                            ) as HTMLInputElement | null;
                                            ref?.select();
                                            ref?.focus();
                                          } else {
                                            const motivoInput = document.getElementById(
                                              "factura-anulada-motivo",
                                            ) as HTMLInputElement | null;
                                            motivoInput?.focus();
                                            motivoInput?.select();
                                          }
                                        }
                                      }}
                                      onKeyDown={(event) => {
                                        if (event.key === "Backspace") {
                                          event.preventDefault();
                                          const actual = nuevaFacturaAnulada[campo.campo] ?? "";
                                          const chars = Array.from({ length: longitud }, (_, pos) => actual[pos] ?? "");
                                          if (chars[idx]) {
                                            chars[idx] = "";
                                            onNuevaFacturaChange(campo.campo, chars.join(""));
                                          } else if (indiceGlobal > 0) {
                                            const anterior = document.getElementById(
                                              construirIdFacturaAnulada(indiceGlobal - 1),
                                            ) as HTMLInputElement | null;
                                            anterior?.focus();
                                            anterior?.select();
                                          }
                                        }
                                      }}
                                      style={{
                                        width: 34,
                                        minWidth: 34,
                                        textAlign: "center",
                                        padding: "0.2rem 0",
                                      }}
                                    />
                                  );
                                })}
                              </div>
                            </td>
                          );
                        });
                      })()}
                      <td>
                        <Input
                          id="factura-anulada-motivo"
                          type="text"
                          value={nuevaFacturaAnulada.motivo}
                          onChange={(event) => {
                            const valor = event.target.value;
                            const procesado = valor.replace(/\s+/g, " ").replace(/^\s+/, "");
                            onNuevaFacturaChange("motivo", procesado);
                          }}
                          maxLength={160}
                        />
                      </td>
                      <td>
                        <Button
                          color="success"
                          type="submit"
                          className="w-100"
                          disabled={
                            !nuevaFacturaAnulada.establecimiento ||
                            !nuevaFacturaAnulada.punto ||
                            !nuevaFacturaAnulada.numero ||
                            !nuevaFacturaAnulada.motivo.trim()
                          }
                        >
                          Agregar
                        </Button>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Form>
              <div className="table-responsive mt-4">
                <Table className="align-middle">
                  <thead>
                    <tr>
                      <th>Factura</th>
                      <th>Motivo</th>
                      <th className="text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {facturasAnuladas.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="text-center text-muted">
                          Sin facturas anuladas.
                        </td>
                      </tr>
                    ) : (
                      facturasAnuladas.map((factura) => (
                        <tr key={factura.id}>
                          <td>{formatearFacturaSegmento(factura) ?? "-"}</td>
                          <td>{factura.motivo}</td>
                          <td className="text-center d-flex gap-2 justify-content-center">
                            {onEditarFactura && (
                              <Button
                                color="link"
                                className="text-primary"
                                onClick={() => onEditarFactura(factura)}
                              >
                                Editar
                              </Button>
                            )}
                            <Button color="link" className="text-danger" onClick={() => onEliminarFactura(factura.id)}>
                              Eliminar
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </CardBody>
      </Card>
    </Col>
  </Row>
);

export default FacturasAnuladasStep;
